import type { IncidentSeverity, JournalIncident } from "@gb/contracts";

/**
 * Tailwind classes per severity. Shared by the list and the detail screen so
 * the two cannot drift into showing the same day in different colours.
 */
export const SEVERITY_STYLES: Record<IncidentSeverity, string> = {
  none: "border-border bg-canvas text-muted",
  minor: "border-warn/40 bg-warn/10 text-warn",
  reportable: "border-danger/40 bg-danger/10 text-danger",
};

/**
 * Fold the form's single-incident editor back into the stored array.
 *
 * The form edits **only the first** incident — a repeating editor is a screen's
 * worth of work on its own — but an entry may hold up to 50, and the detail
 * screen renders all of them. Rebuilding the array from the form's two fields
 * would therefore delete every incident after the first, silently, on a save
 * that only meant to change the headcount.
 *
 * So the tail is carried through untouched. The form tells the user when there
 * is a tail, because an editor that quietly governs only part of what is on
 * screen is worse than one that says so.
 */
export function mergeIncidents(
  existing: readonly JournalIncident[],
  severity: IncidentSeverity,
  text: string,
): JournalIncident[] {
  const tail = existing.slice(1);
  const trimmed = text.trim();

  // No text and no severity is "nothing happened", which is recorded as an
  // empty list rather than a placeholder row.
  if (!trimmed && severity === "none") return tail;

  return [{ severity, text: trimmed || "No detail recorded." }, ...tail];
}

/** How many incidents the form is not showing. */
export function hiddenIncidentCount(existing: readonly JournalIncident[]): number {
  return Math.max(0, existing.length - 1);
}
